import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { ENTERPRISE_REVENUE_OPTIONS } from "@/constants/pricing";
import {
  enterprisePlanService,
  type EnterprisePlanRequestInput,
} from "@/services/enterprisePlanService";

interface EnterpriseContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Prefilled from the brand profile / an existing request so the form is one click for most stores */
  defaultValues?: {
    contactName?: string;
    email?: string;
    companyName?: string;
  };
  /** Store already has an open request, so this submission updates it */
  hasOpenRequest?: boolean;
}

type FormState = {
  contactName: string;
  email: string;
  phone: string;
  companyName: string;
  monthlyRevenue: string;
  message: string;
};

const emptyForm: FormState = {
  contactName: "",
  email: "",
  phone: "",
  companyName: "",
  monthlyRevenue: "",
  message: "",
};

export function EnterpriseContactModal({
  isOpen,
  onClose,
  defaultValues,
  hasOpenRequest = false,
}: EnterpriseContactModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState("");

  // Reset to the latest known contact details every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setForm({
      ...emptyForm,
      contactName: defaultValues?.contactName ?? "",
      email: defaultValues?.email ?? "",
      companyName: defaultValues?.companyName ?? "",
    });
    setError("");
  }, [
    isOpen,
    defaultValues?.contactName,
    defaultValues?.email,
    defaultValues?.companyName,
  ]);

  const { mutate: submitRequest, isPending } = useMutation({
    mutationFn: (input: EnterprisePlanRequestInput) =>
      enterprisePlanService.submitRequest(input),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["enterprise-plan-request"] });
      toast.success(
        result.isUpdate
          ? "Your request has been updated — we'll be in touch."
          : "Thanks! Our team will reach out about a custom plan.",
      );
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? "Failed to send your request. Please try again.";
      const text = Array.isArray(message) ? message[0] : message;
      setError(text);
      toast.error(text);
    },
  });

  const handleSubmit = () => {
    const contactName = form.contactName.trim();
    const email = form.email.trim();

    if (!contactName) {
      setError("Please tell us who we should contact.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    submitRequest({
      contactName,
      email,
      phone: form.phone.trim() || undefined,
      companyName: form.companyName.trim() || undefined,
      monthlyRevenue: form.monthlyRevenue || undefined,
      message: form.message.trim() || undefined,
    });
  };

  const inputClass =
    "w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-muted";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => !isPending && onClose()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-card p-6 shadow-2xl transition-all sm:p-8">
                <button
                  onClick={onClose}
                  disabled={isPending}
                  className="absolute top-4 right-4 rounded-full p-1.5 text-muted-foreground hover:bg-accent hover:text-muted-foreground transition-colors disabled:opacity-50"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                <Dialog.Title
                  as="h2"
                  className="text-2xl font-bold text-foreground pr-8"
                >
                  {hasOpenRequest
                    ? "Update your Enterprise request"
                    : "Talk to us about Enterprise"}
                </Dialog.Title>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tell us about your store and our team will put together a
                  custom plan — pricing, revenue fee and limits tailored to you.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="enterprise-name"
                        className="block text-sm font-medium text-muted-foreground mb-1.5"
                      >
                        Your name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="enterprise-name"
                        type="text"
                        value={form.contactName}
                        onChange={(e) =>
                          setForm({ ...form, contactName: e.target.value })
                        }
                        disabled={isPending}
                        placeholder="Jane Doe"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="enterprise-email"
                        className="block text-sm font-medium text-muted-foreground mb-1.5"
                      >
                        Work email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="enterprise-email"
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        disabled={isPending}
                        placeholder="jane@brand.com"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="enterprise-company"
                        className="block text-sm font-medium text-muted-foreground mb-1.5"
                      >
                        Company
                      </label>
                      <input
                        id="enterprise-company"
                        type="text"
                        value={form.companyName}
                        onChange={(e) =>
                          setForm({ ...form, companyName: e.target.value })
                        }
                        disabled={isPending}
                        placeholder="Brand name"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="enterprise-phone"
                        className="block text-sm font-medium text-muted-foreground mb-1.5"
                      >
                        Phone
                      </label>
                      <input
                        id="enterprise-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        disabled={isPending}
                        placeholder="Optional"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="enterprise-revenue"
                      className="block text-sm font-medium text-muted-foreground mb-1.5"
                    >
                      Monthly revenue
                    </label>
                    <select
                      id="enterprise-revenue"
                      value={form.monthlyRevenue}
                      onChange={(e) =>
                        setForm({ ...form, monthlyRevenue: e.target.value })
                      }
                      disabled={isPending}
                      className={inputClass}
                    >
                      <option value="">Prefer not to say</option>
                      {ENTERPRISE_REVENUE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="enterprise-message"
                      className="block text-sm font-medium text-muted-foreground mb-1.5"
                    >
                      What do you need?
                    </label>
                    <textarea
                      id="enterprise-message"
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      disabled={isPending}
                      rows={4}
                      maxLength={2000}
                      placeholder="Affiliate volume, custom revenue fee, onboarding support…"
                      className={inputClass}
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600" role="alert">
                      {error}
                    </p>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                    <button
                      onClick={onClose}
                      disabled={isPending}
                      className="px-4 py-2.5 rounded-lg border border-border text-muted-foreground font-semibold hover:bg-accent transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isPending}
                      className="px-5 py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPending ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending…
                        </span>
                      ) : hasOpenRequest ? (
                        "Update request"
                      ) : (
                        "Send request"
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
